import { useCallback, type ReactNode } from "react";
import { Badge } from "../Badge/Badge";
import styles from "./ShiftRow.module.css";

export type ShiftRowStatus =
  | "no_show"
  | "late"
  | "due_soon"
  | "on_site"
  | "completed"
  | "upcoming";

interface ShiftRowProps {
  readonly cleanerName: string;
  readonly siteName: string;
  readonly timeRange: string;
  readonly status: ShiftRowStatus;
  readonly expanded: boolean;
  readonly onToggle: () => void;
  readonly children?: ReactNode;
}

const statusBadge: Record<ShiftRowStatus, { label: string; variant: "danger" | "warning" | "success" | "neutral" } | null> = {
  no_show: { label: "No show", variant: "danger" },
  late: { label: "Late", variant: "warning" },
  due_soon: { label: "Due soon", variant: "warning" },
  on_site: { label: "On site", variant: "success" },
  completed: { label: "Done", variant: "neutral" },
  upcoming: null,
};

const statusStrip: Record<ShiftRowStatus, string> = {
  no_show: styles.stripDanger,
  late: styles.stripWarning,
  due_soon: styles.stripWarning,
  on_site: styles.stripSuccess,
  completed: styles.stripNeutral,
  upcoming: styles.stripNone,
};

export function ShiftRow({
  cleanerName,
  siteName,
  timeRange,
  status,
  expanded,
  onToggle,
  children,
}: ShiftRowProps) {
  const badge = statusBadge[status];
  const strip = statusStrip[status];

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onToggle();
      }
    },
    [onToggle],
  );

  return (
    <div>
      <div
        className={styles.row}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={onToggle}
        onKeyDown={handleKeyDown}
      >
        <div className={`${styles.strip} ${strip}`} />
        <div className={styles.content}>
          <div className={styles.line1}>
            <span className={styles.cleanerName}>{cleanerName}</span>
            <span className={styles.siteName}>{siteName}</span>
          </div>
          <div className={styles.line2}>
            <span className={styles.time}>{timeRange}</span>
            {badge && <Badge variant={badge.variant}>{badge.label}</Badge>}
          </div>
        </div>
        <svg
          className={`${styles.chevron} ${expanded ? styles.chevronOpen : ""}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </div>
      {expanded && (
        <div className={styles.detailInner}>
          {children}
        </div>
      )}
    </div>
  );
}

export function ShiftRowDetail({
  children,
}: {
  readonly children: ReactNode;
}) {
  return <div className={styles.detailRow}>{children}</div>;
}

export function ShiftRowCallLink({
  name,
  phone,
}: {
  readonly name: string;
  readonly phone: string;
}) {
  return (
    <a href={`tel:${phone}`} className={styles.callLink}>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M3.654 1.328a.678.678 0 00-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 004.168 6.608 17.569 17.569 0 006.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 00-.063-1.015l-2.307-1.794a.678.678 0 00-.58-.122l-2.19.547a1.745 1.745 0 01-1.657-.459L5.482 8.062a1.745 1.745 0 01-.46-1.657l.548-2.19a.678.678 0 00-.122-.58L3.654 1.328z" />
      </svg>
      Call {name.split(" ")[0]}
    </a>
  );
}

export function ShiftRowVerification({
  verification,
}: {
  readonly verification: string;
}) {
  const labels: Record<string, string> = {
    verified: "GPS verified",
    manual_override: "Manual override",
    gps_unavailable: "GPS unavailable",
    gps_timeout: "GPS timeout",
    auto_closed: "Auto closed",
  };
  return (
    <span className={styles.verificationBadge}>
      {labels[verification] ?? verification}
    </span>
  );
}
